import React, { useEffect, useState } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Empty,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { errorMessage, type AdminApi, type LibraryCardRow } from '../api';
import type { BillingApi } from '../billingApi';
import { CardPreviewModal } from '../components/CardPreview';

const { Text } = Typography;

const PAGE_SIZE = 50;
const TYPES = [
  { value: 'noun', label: 'Nouns' },
  { value: 'verb', label: 'Verbs' },
  { value: 'adjective', label: 'Adjectives' },
  { value: 'adverb', label: 'Adverbs' },
  { value: 'phrase', label: 'Phrases' },
  { value: 'other', label: 'Other words' },
];
const TYPE_LABEL: Record<string, string> = {
  noun: 'Noun',
  verb: 'Verb',
  adjective: 'Adjective',
  adverb: 'Adverb',
  phrase: 'Phrase',
  other: 'Word',
};
const GENDER_COLOR: Record<string, string> = {
  masculine: '#2563eb',
  feminine: '#dc2626',
  neuter: '#16a34a',
};

function pictureCell(row: LibraryCardRow): React.ReactNode {
  if (row.image_url) {
    return (
      <img
        src={row.image_url}
        alt=""
        loading="lazy"
        style={{ width: 64, height: 36, objectFit: 'cover', borderRadius: 4, display: 'block' }}
      />
    );
  }
  if (row.part_of_speech !== 'noun' && row.part_of_speech !== 'verb') {
    return <Text type="secondary">None</Text>;
  }
  return row.image_status === 'failed' ? (
    <Tag color="red">Failed</Tag>
  ) : (
    <Tag color="orange">Not yet</Tag>
  );
}

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

interface Query {
  search: string;
  type: string | null;
  page: number;
}

/**
 * Every card in the shared library: the one copy of each word that all learners get, whoever
 * added it first (a learner, an import or a public deck). Read-only, newest first.
 */
export const CardsScreen: React.FC<{ api: AdminApi; billing?: BillingApi }> = ({
  api,
  billing,
}) => {
  const { message } = AntApp.useApp();
  // Library words the owner keeps off the public website's word pages (this page's rows only).
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState<Query>({ search: '', type: null, page: 1 });
  const [result, setResult] = useState<{ cards: LibraryCardRow[]; total: number } | null>(null);
  const [loaded, setLoaded] = useState<Query | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [viewing, setViewing] = useState<LibraryCardRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listCards({
        search: query.search,
        partOfSpeech: query.type,
        page: query.page,
        pageSize: PAGE_SIZE,
      })
      .then((r) => {
        if (cancelled) return;
        setResult(r);
        setLoaded(query);
        setLoadError(null);
        billing
          ?.hiddenWords(r.cards.map((c) => c.id))
          .then((ids) => {
            if (!cancelled) setHidden(new Set(ids));
          })
          .catch(() => {
            /* the switches stay off; changing one reports its own error */
          });
      })
      .catch((e) => {
        if (cancelled) return;
        setLoaded(query);
        setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [api, billing, query, reloadKey]);

  const toggleHidden = async (row: LibraryCardRow, value: boolean) => {
    const flip = (on: boolean) =>
      setHidden((prev) => {
        const next = new Set(prev);
        if (on) next.add(row.id);
        else next.delete(row.id);
        return next;
      });
    flip(value);
    try {
      await billing!.setSiteHidden(row.id, value);
    } catch (e) {
      flip(!value);
      message.error(errorMessage(e));
    }
  };

  const loading = loaded !== query && !loadError;
  const filtered = !!query.search || !!query.type;

  const columns = [
    {
      title: 'Picture',
      key: 'picture',
      width: 96,
      render: (_: unknown, row: LibraryCardRow) => pictureCell(row),
    },
    {
      title: 'Word',
      key: 'lemma',
      render: (_: unknown, row: LibraryCardRow) => (
        <span lang="de">
          {row.article && (
            <span style={{ color: (row.gender && GENDER_COLOR[row.gender]) || undefined }}>
              {row.article}{' '}
            </span>
          )}
          <strong>{row.lemma}</strong>
        </span>
      ),
    },
    { title: 'Meaning', dataIndex: 'translation', key: 'translation' },
    {
      title: 'Type',
      key: 'type',
      width: 110,
      render: (_: unknown, row: LibraryCardRow) =>
        TYPE_LABEL[row.part_of_speech] ?? row.part_of_speech,
    },
    {
      title: 'Languages',
      key: 'languages',
      width: 110,
      render: (_: unknown, row: LibraryCardRow) => (
        <Text type={row.translation_count >= 8 ? undefined : 'secondary'}>
          {row.translation_count} of 8
        </Text>
      ),
    },
    {
      title: 'Text',
      key: 'source',
      width: 100,
      render: (_: unknown, row: LibraryCardRow) =>
        row.content_source === 'curated' ? (
          <Tag color="purple" title="Written and checked by a person for a LingoPing deck">
            Hand-written
          </Tag>
        ) : (
          <Tag title="Written by the AI when the word was first added">AI</Tag>
        ),
    },
    {
      title: 'First created by',
      key: 'created_by',
      render: (_: unknown, row: LibraryCardRow) =>
        row.created_by === 'Admin' ? (
          <Tag color="blue">Admin</Tag>
        ) : row.created_by ? (
          <Text>{row.created_by}</Text>
        ) : (
          <Text
            type="secondary"
            title="Created before this was recorded"
            style={{ whiteSpace: 'nowrap' }}
          >
            Not recorded
          </Text>
        ),
    },
    {
      title: 'Learners',
      key: 'learners',
      width: 90,
      align: 'right' as const,
      render: (_: unknown, row: LibraryCardRow) => (
        <Text type={row.learners > 0 ? undefined : 'secondary'}>{row.learners}</Text>
      ),
    },
    {
      title: 'Created',
      key: 'created',
      width: 130,
      render: (_: unknown, row: LibraryCardRow) => (
        <Text type="secondary">{day(row.created_at)}</Text>
      ),
    },
    ...(billing
      ? [
          {
            title: 'Hidden from website',
            key: 'site_hidden',
            width: 150,
            render: (_: unknown, row: LibraryCardRow) => (
              <Switch
                size="small"
                aria-label={`Hide ${row.lemma} from the website`}
                checked={hidden.has(row.id)}
                onChange={(value) => void toggleHidden(row, value)}
              />
            ),
          },
        ]
      : []),
    {
      title: '',
      key: 'view',
      width: 60,
      render: (_: unknown, row: LibraryCardRow) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          aria-label={`View the card of ${row.lemma}`}
          title="View card"
          onClick={() => setViewing(row)}
        />
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={3} style={{ margin: 0 }}>
        Cards
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ margin: '4px 0 16px' }}>
        Every card in the shared library. Each word has one card, and every learner who adds that
        word gets the same one. Newest first. “Learners” counts the accounts that have started the
        word (learning, learned or relearning; not words still waiting, and not ones marked as
        known). Notes that learners write on their cards are private and never shown here.
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Search a word or its meaning"
          aria-label="Search cards"
          style={{ width: 300 }}
          onSearch={(value) => setQuery((q) => ({ ...q, search: value.trim(), page: 1 }))}
        />
        <Select
          allowClear
          placeholder="All types"
          aria-label="Type of word"
          style={{ width: 170 }}
          options={TYPES}
          value={query.type}
          onChange={(value) => setQuery((q) => ({ ...q, type: value ?? null, page: 1 }))}
        />
        {result && !loadError && (
          <Text type="secondary">
            {result.total.toLocaleString('en')} card{result.total === 1 ? '' : 's'}
            {filtered ? ' found' : ' in total'}
          </Text>
        )}
      </Space>

      {loadError && (
        <Alert
          type="error"
          showIcon
          title="Could not load the cards"
          description={loadError}
          action={<Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<LibraryCardRow>
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={loadError ? [] : (result?.cards ?? [])}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: query.page,
          pageSize: PAGE_SIZE,
          total: result?.total ?? 0,
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
        locale={{
          emptyText: (
            <Empty
              description={
                filtered ? 'No card matches this search.' : 'There are no cards in the library yet.'
              }
            />
          ),
        }}
      />

      <CardPreviewModal
        source={
          viewing
            ? { key: viewing.id, lemma: viewing.lemma, load: () => api.getLibraryCard(viewing.id) }
            : null
        }
        onClose={() => setViewing(null)}
      />
    </div>
  );
};
